import React, { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { DatePicker, Empty, Form, Input, List, Modal, Typography } from 'antd';
import moment from 'moment';

import { CommentHistoryItem, UserUpdate } from '../../../../../../../../app/types/unApi';
import { formatCommentDate } from '../../../../../../../../helpers/utils/time';
import { showErrorMessage } from '../../../../../../../../helpers/utils/ui';
import { unApi } from '../../../../../../api/endpoints/reports/unApi';

type Props = {
    visible:boolean
    id:number
    setVisible: React.Dispatch<React.SetStateAction<{visible: boolean, id: number}>>
    loadRows:()=>void
}
const EditUser = ({ visible,id,setVisible,loadRows }:Props) => {
    const [ form ] = Form.useForm();
    const [ formLoading, setFormLoading ] = useState(false);
    // История комментариев (read-only, новые сверху)
    const [ commentHistory, setCommentHistory ] = useState<CommentHistoryItem[]>([]);

    const fetchData =  async () => {
        setFormLoading(true);
        try{
             const response = await unApi.getUserInfo(Number(id));
            form.resetFields();
            const { created_at, email, name, comment, comment_history } = response.data;
            const formattedDate = moment(created_at);
            form.setFieldsValue({
                name: name,
                created_at: formattedDate,
                email: email,
                // Поле нового комментария оставляем пустым — старые не затираем
                comment: ''
            });
            if (Array.isArray(comment_history) && comment_history.length > 0) {
                setCommentHistory(comment_history);
            } else if (comment) {
                // Легаси: истории нет, но есть старый комментарий — показываем без автора/даты
                setCommentHistory([ { comment, user_name: '', created_at: '' } ]);
            } else {
                setCommentHistory([]);
            }
        }catch(err){
            showErrorMessage('Произошла ошибка, повторите попытку позже.');
        }finally{
            setFormLoading(false);

        }
    };

    useEffect(() => {
        if (visible) {
            fetchData();
        }
    }, [ visible ]);

    const editUser = async (values: UserUpdate) => {
        const data = {
            user_id:id,
            email:values.email,
            created_at:moment(values.created_at).format('YYYY-MM-DD'),
            comment:values.comment ?? ''
        };
        setFormLoading(true);
        try{
            const response = await unApi.updateUserInfo(data);
            if(response.status === 200){
                form.resetFields();
                setVisible({ visible:false,id:0 });
                loadRows();
                Modal.info({
                    title: 'Успех!',
                    content: 'Информация об пользователе обновлена.',
                });
            }
        }catch(err){
            showErrorMessage('Произошла ошибка, повторите попытку позже.');

        }finally{
            setFormLoading(false);
        }
    };

    return (
        <Modal
            title="Обновление Пользователя"
            open={visible}
            onCancel={() => {setVisible({ visible:false,id:0 });}}
            onOk={() => {form.submit();}}
            okButtonProps={{ loading: formLoading }}
            okText="Сохранить"
        >
            {
                formLoading ?
                    <div className="loading-container">
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                    </div>
                    :
                    <Form
                        form={form}
                        onFinish={editUser}
                        requiredMark={false}
                    >
                        <Form.Item
                            name="name"
                            label="Имя пользователя"
                            rules={[ { required: true } ]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="created_at"
                            label="Дата создания"
                            rules={[ { required: true } ]}
                        >
                            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                            {/* @ts-ignore */}
                            <DatePicker style={{ marginLeft:26 }} format="DD/MM/YYYY" />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true },
                                { type: 'email', message: 'Введите корректный email' }
                            ]}
                        >
                            <Input style={{ marginLeft:84,width:'200px' }} />
                        </Form.Item>

                        <Form.Item
                            name="comment"
                            label="Новый комментарий"
                            rules={[ { required: false } ]}
                        >
                            <Input.TextArea placeholder="Добавить комментарий…" />
                        </Form.Item>

                        <div style={{ marginTop: 12 }}>
                            <Typography.Text strong>История комментариев</Typography.Text>
                            {commentHistory.length === 0 ? (
                                <Empty description="Комментариев ещё нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <List
                                    size="small"
                                    dataSource={commentHistory}
                                    style={{ maxHeight: 240, overflowY: 'auto' }}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <div>
                                                <div style={{ whiteSpace: 'pre-wrap' }}>{item.comment}</div>
                                                {(item.user_name || item.created_at) && (
                                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                        {item.user_name}
                                                        {item.user_name && item.created_at ? ' · ' : ''}
                                                        {formatCommentDate(item.created_at)}
                                                    </Typography.Text>
                                                )}
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </div>
                    </Form>
            }
        </Modal>
    );
};

export default EditUser;
